import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GenericModal from './GenericModal';
import objetivosApi from '../../api/objetivos';
import { useGoogleAds } from '../../hooks/useGoogleAds';
import { useMetaAds } from '../../hooks/useMetaAds';
import { GoogleIcon, MetaIcon } from '../index';
import { normalizeCurrencyCode } from '../../utils/currency';

const PLATFORM_GOOGLE = 'GOOGLE';
const PLATFORM_META = 'META';

const METRICS_NEEDING_CONVERSIONS = ['Conversiones', 'ROAS', 'CPA'];

function objectiveNeedsConversions(metrica) {
  return METRICS_NEEDING_CONVERSIONS.includes(metrica || '');
}

function relationToId(item) {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return String(item.id ?? '');
}

function unwrapObjetivoResponse(res) {
  const d = res?.data;
  if (d && typeof d === 'object' && d.objetivo) return d.objetivo;
  return d;
}

function buildConversionesRelacionadas(selectedIds, availableById, originalRelations) {
  const origById = new Map();
  (originalRelations || []).forEach((item) => {
    const id = relationToId(item);
    if (id) origById.set(id, item);
  });
  return selectedIds.map((id) => {
    const fromAvail = availableById.get(id);
    if (fromAvail) {
      return {
        id: fromAvail.id,
        nombre: fromAvail.nombre,
        plataforma: fromAvail.plataforma,
      };
    }
    const orig = origById.get(id);
    if (orig && typeof orig === 'object') return orig;
    return { id };
  });
}

function stripIdForPut(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const { id: _omit, ...rest } = obj;
  return rest;
}

/**
 * Modal apilado para editar solo target y conversiones (si aplica) sin salir del flujo de setup.
 */
const QuickEditObjectiveModal = ({
  isOpen,
  onClose,
  objectiveId,
  onSaved,
  overlayZIndex = 10100,
}) => {
  const {
    isConnected: isGoogleConnected,
    activeAccount: activeGoogleAccount,
  } = useGoogleAds();
  const {
    isConnected: isMetaConnected,
    activeAccount: activeMetaAccount,
  } = useMetaAds();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverObj, setServerObj] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [targetInput, setTargetInput] = useState('');
  const [selectedConversionIds, setSelectedConversionIds] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const allConversions = useMemo(() => {
    const list = [];
    if (isGoogleConnected && activeGoogleAccount) {
      const googleConversiones =
        (activeGoogleAccount.conversion_actions || [])
          ?.filter((action) => action && action.status === 'ENABLED')
          .map((action) => ({
            id: String(action?.id ?? ''),
            nombre: action?.name || '',
            plataforma: PLATFORM_GOOGLE,
            monthlyConversions:
              Number(action?.monthly_conversions ?? action?.['metrics.conversions'] ?? 0) || 0,
          })) || [];
      list.push(...googleConversiones);
    }
    if (isMetaConnected && activeMetaAccount) {
      const metaConversiones =
        (activeMetaAccount.pixels?.data || [])?.flatMap((pixel) =>
          (pixel?.existing_events || [])?.map((event) => ({
            id: `${pixel?.id || ''}_${event || ''}`,
            nombre: `${pixel?.name || ''} - ${event || ''}`,
            plataforma: PLATFORM_META,
            monthlyConversions: Number(pixel?.monthly_event_counts?.[event] ?? 0) || 0,
          })) || []
        ) || [];
      list.push(...metaConversiones);
    }
    return list.filter((c) => c.id);
  }, [isGoogleConnected, activeGoogleAccount, isMetaConnected, activeMetaAccount]);

  const filteredConversions = useMemo(() => {
    const plats = serverObj?.plataformas_relacionadas;
    if (!Array.isArray(plats) || plats.length === 0) return allConversions;
    const allowed = new Set(plats.map((p) => String(p).toUpperCase()));
    return allConversions.filter((c) => allowed.has(String(c.plataforma).toUpperCase()));
  }, [allConversions, serverObj?.plataformas_relacionadas]);

  const googleConv = useMemo(
    () => filteredConversions.filter((c) => String(c.plataforma).toUpperCase() === PLATFORM_GOOGLE),
    [filteredConversions]
  );
  const metaConv = useMemo(
    () => filteredConversions.filter((c) => String(c.plataforma).toUpperCase() === PLATFORM_META),
    [filteredConversions]
  );

  const availableById = useMemo(() => {
    const m = new Map();
    filteredConversions.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [filteredConversions]);

  /** Misma lógica que el modal de Objetivos: ISO para prefijo del input y para PUT (CPA / Presupuesto). */
  const monedaInputISO = useMemo(() => {
    if (!serverObj) return '';
    const fromObj = normalizeCurrencyCode(serverObj.currency);
    if (fromObj) return fromObj;
    const plat = serverObj.plataformas_relacionadas?.[0];
    const key = plat != null ? String(plat).toLowerCase() : '';
    if (key === 'google') {
      return normalizeCurrencyCode(activeGoogleAccount?.currency) || activeGoogleAccount?.currency || '';
    }
    if (key === 'meta') {
      return normalizeCurrencyCode(activeMetaAccount?.currency) || activeMetaAccount?.currency || '';
    }
    return (
      normalizeCurrencyCode(activeGoogleAccount?.currency || activeMetaAccount?.currency) ||
      activeGoogleAccount?.currency ||
      activeMetaAccount?.currency ||
      'USD'
    );
  }, [serverObj, activeGoogleAccount, activeMetaAccount]);

  useEffect(() => {
    if (!isOpen || !objectiveId) {
      return undefined;
    }
    let cancelled = false;
    setLoadError(null);
    setSaveError(null);
    setServerObj(null);
    setNameInput('');
    setTargetInput('');
    setSelectedConversionIds([]);
    setLoading(true);

    (async () => {
      try {
        const res = await objetivosApi.getObjetivo(objectiveId);
        const data = unwrapObjetivoResponse(res);
        if (cancelled) return;
        if (!data || data.id == null) {
          setLoadError('No se pudo cargar el objetivo.');
          return;
        }
        setServerObj(data);
        setNameInput(data?.nombre != null ? String(data.nombre) : '');
        const t = data.target;
        setTargetInput(t != null && t !== '' ? String(t) : '');
        const rel = data.conversiones_relacionadas || [];
        setSelectedConversionIds(rel.map(relationToId).filter(Boolean));
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err?.response?.data?.error || err?.response?.data?.message || 'Error al cargar el objetivo.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, objectiveId]);

  const toggleConversion = useCallback((id) => {
    const sid = String(id);
    setSelectedConversionIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  }, []);

  const selectAllForPlatform = useCallback(
    (platform) => {
      const ids =
        platform === PLATFORM_GOOGLE
          ? googleConv.map((c) => String(c.id))
          : metaConv.map((c) => String(c.id));
      setSelectedConversionIds((prev) => {
        const without = prev.filter((p) => !ids.includes(p));
        const allSelected = ids.length > 0 && ids.every((id) => prev.includes(id));
        return allSelected ? without : [...new Set([...without, ...ids])];
      });
    },
    [googleConv, metaConv]
  );

  const metrica = serverObj?.metrica_relacionada || '';
  const showConversions = objectiveNeedsConversions(metrica);
  const hasConversionList = filteredConversions.length > 0;

  const modalTitle = useMemo(() => {
    const m = serverObj?.metrica_relacionada;
    if (!m) return 'Editar objetivo';
    if (m === 'Presupuesto') return 'Editar Inversión';
    return `Editar ${m}`;
  }, [serverObj?.metrica_relacionada]);

  const targetLabel = useMemo(() => {
    switch (metrica) {
      case 'ROAS':
        return 'Objetivo de ROAS';
      case 'CPA':
        return 'Objetivo de CPA';
      case 'Conversiones':
        return 'Objetivo de Conversiones';
      case 'Presupuesto':
        return 'Objetivo de Inversión';
      default:
        return 'Objetivo';
    }
  }, [metrica]);

  const targetHint = useMemo(() => {
    if (metrica === 'ROAS') return 'Ej.: 4.26 para un ROAS de 4.26x';
    return null;
  }, [metrica]);

  const showCurrencyPrefix =
    (metrica === 'CPA' || metrica === 'Presupuesto') && Boolean(monedaInputISO);

  const targetInputStep = metrica === 'Conversiones' ? '1' : '0.01';

  const targetPlaceholder = useMemo(() => {
    switch (metrica) {
      case 'ROAS':
        return '3.5';
      case 'CPA':
        return '50';
      case 'Conversiones':
        return '100';
      case 'Presupuesto':
        return '1000';
      default:
        return '';
    }
  }, [metrica]);

  const validate = useCallback(() => {
    if (!String(nameInput).trim()) {
      return 'El nombre del objetivo no puede estar vacío.';
    }
    const num = parseFloat(String(targetInput).replace(',', '.'));
    if (!num || num <= 0 || Number.isNaN(num)) {
      return 'El valor objetivo debe ser un número mayor a 0.';
    }
    if (showConversions && hasConversionList && selectedConversionIds.length === 0) {
      return 'Elegí al menos una conversión para esta métrica.';
    }
    return null;
  }, [nameInput, targetInput, showConversions, hasConversionList, selectedConversionIds.length]);

  const performSave = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      setSaveError(validationError);
      return;
    }
    if (!serverObj?.id) return;

    setSaving(true);
    setSaveError(null);
    const numTarget = parseFloat(String(targetInput).replace(',', '.'));
    const originalRel = serverObj.conversiones_relacionadas || [];
    const conversionesPayload = showConversions
      ? buildConversionesRelacionadas(selectedConversionIds, availableById, originalRel)
      : originalRel;

    const base = stripIdForPut(serverObj);
    const objetivoData = {
      ...base,
      nombre: String(nameInput).trim(),
      target: numTarget,
      ...(showConversions ? { conversiones_relacionadas: conversionesPayload } : {}),
    };
    if (
      (metrica === 'CPA' || metrica === 'Presupuesto') &&
      monedaInputISO &&
      normalizeCurrencyCode(monedaInputISO)
    ) {
      objetivoData.currency = normalizeCurrencyCode(monedaInputISO);
    }

    try {
      const putRes = await objetivosApi.updateObjetivo(objectiveId, objetivoData);
      let updated = unwrapObjetivoResponse(putRes);
      if (!updated || typeof updated !== 'object' || updated.id == null) {
        const getRes = await objetivosApi.getObjetivo(objectiveId);
        updated = unwrapObjetivoResponse(getRes);
      }
      if (updated && typeof onSaved === 'function') {
        onSaved(updated);
      }
      onClose();
    } catch (err) {
      setSaveError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'No se pudieron guardar los cambios. Probá de nuevo.'
      );
    } finally {
      setSaving(false);
    }
  }, [
    validate,
    serverObj,
    targetInput,
    nameInput,
    showConversions,
    selectedConversionIds,
    availableById,
    objectiveId,
    onSaved,
    onClose,
    metrica,
    monedaInputISO,
  ]);

  const handleRetrySave = useCallback(() => {
    performSave();
  }, [performSave]);

  const ConversionBlock = ({ title, icon: Icon, conversions, platform }) => {
    if (conversions.length === 0) return null;
    const ids = conversions.map((c) => String(c.id));
    const allSelected = ids.length > 0 && ids.every((id) => selectedConversionIds.includes(id));
    const sorted = [...conversions].sort((a, b) => {
      const aSel = selectedConversionIds.includes(String(a.id));
      const bSel = selectedConversionIds.includes(String(b.id));
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      const aM = Number(a?.monthlyConversions ?? 0) || 0;
      const bM = Number(b?.monthlyConversions ?? 0) || 0;
      if (bM !== aM) return bM - aM;
      return String(a?.nombre ?? '').localeCompare(String(b?.nombre ?? ''), 'es', {
        sensitivity: 'base',
      });
    });
    return (
      <div className="w-full rounded-xl bg-white/5 border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <Icon width={20} height={20} className="shrink-0" />
            <span className="font-semibold text-white text-sm truncate">{title}</span>
            <span className="text-white/50 text-xs shrink-0">({conversions.length})</span>
          </div>
          <button
            type="button"
            onClick={() => selectAllForPlatform(platform)}
            className="shrink-0 rounded-md px-2 py-1.5 text-xs font-medium text-primary-light hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            {allSelected ? 'Desmarcar todas' : 'Seleccionar todas'}
          </button>
        </div>
        <div className="max-h-36 overflow-y-auto divide-y divide-white/5">
          {sorted.map((conv) => {
            const cid = String(conv.id);
            return (
              <label
                key={cid}
                className="flex min-h-11 items-center gap-3 px-4 py-2 hover:bg-white/5 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedConversionIds.includes(cid)}
                  onChange={() => toggleConversion(cid)}
                  className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/10 text-primary focus:outline-none focus:ring-2 focus:ring-primary/60 focus:ring-offset-2 focus:ring-offset-slate-900/80"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-white/90 truncate block">{conv.nombre}</span>
                  <span className="text-[11px] text-white/60">
                    Últimos 30 días: {Number(conv?.monthlyConversions ?? 0).toLocaleString('es-ES')}
                  </span>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <GenericModal
      isOpen={isOpen}
      toggle={onClose}
      title={modalTitle}
      onClose={onClose}
      showSubmitButton={false}
      showLogo={false}
      width="min(92vw, 480px)"
      contentPadding="1.25rem 1.5rem 1.5rem"
      closable
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      overlayZIndex={overlayZIndex}
    >
      <div className="space-y-4">
        {loading && !loadError && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="h-10 w-10 border-2 border-purple-500/40 border-t-purple-500 rounded-full animate-spin" />
            <p className="text-white/70 text-sm">Cargando…</p>
          </div>
        )}

        {loadError && (
          <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
            {loadError}
          </div>
        )}

        {!loading && !loadError && serverObj && (
          <>
            <div>
              <label htmlFor="quick-edit-name" className="block text-xs font-medium text-white/80 mb-1.5">
                Nombre del objetivo
              </label>
              <input
                id="quick-edit-name"
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/60"
                autoComplete="off"
              />
            </div>

            <div>
              <label htmlFor="quick-edit-target" className="block text-xs font-medium text-white/80 mb-1.5">
                {targetLabel}
              </label>
              <div className="relative">
                {showCurrencyPrefix && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-white/60 font-medium pointer-events-none">
                    {monedaInputISO}
                  </span>
                )}
                <input
                  id="quick-edit-target"
                  type="number"
                  step={targetInputStep}
                  min="0"
                  placeholder={targetPlaceholder}
                  value={targetInput}
                  onChange={(e) => setTargetInput(e.target.value)}
                  className={`w-full rounded-lg border border-white/20 bg-white/10 py-2 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/60 ${
                    showCurrencyPrefix ? 'pl-[2.75rem] pr-3' : 'px-3'
                  }`}
                  autoComplete="off"
                />
              </div>
              {targetHint && <p className="mt-1 text-[11px] text-white/55">{targetHint}</p>}
            </div>

            {showConversions && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-white/80">Conversiones</p>
                {!hasConversionList ? (
                  <p className="text-xs text-white/55">
                    No hay conversiones disponibles en la cuenta para esta plataforma.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <ConversionBlock
                      title="Google Ads"
                      icon={GoogleIcon}
                      conversions={googleConv}
                      platform={PLATFORM_GOOGLE}
                    />
                    <ConversionBlock
                      title="Meta Ads"
                      icon={MetaIcon}
                      conversions={metaConv}
                      platform={PLATFORM_META}
                    />
                  </div>
                )}
              </div>
            )}

            {saveError && (
              <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-50 space-y-2">
                <p>{saveError}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleRetrySave}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-medium disabled:opacity-50"
                  >
                    Reintentar
                  </button>
                  <button
                    type="button"
                    onClick={() => setSaveError(null)}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-md border border-white/25 text-white/90 text-xs font-medium hover:bg-white/10 disabled:opacity-50"
                  >
                    Volver
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 min-h-[44px] rounded-lg border border-white/25 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={performSave}
                disabled={saving}
                className="flex-1 min-h-[44px] rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:from-blue-500 hover:to-purple-500 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Aplicar'}
              </button>
            </div>
          </>
        )}
      </div>
    </GenericModal>
  );
};

export default QuickEditObjectiveModal;
